import { t } from 'i18next';

// Domain
import {
  mapBarChartProps,
  mapLineChartProps,
  mapPieChartProps,
} from '../../../domain/dashboardChart/dashboardChartUseCase';
import { getDashboardItemType } from '../../../services/dashboard/dashboardUseCase';

// Store
import { Popover } from '../../../store/popover/PopoverContext';
import { useDashboardActionContextOptional } from '../../../store/dashboardAction/DashboardActionContext';

// Components
import ChartBar from '../../atoms/ChartBar/ChartBar';
import ChartLine from '../../atoms/ChartLine/ChartLine';
import ChartPie from '../../atoms/ChartPie/ChartPie';
import Iframe from '../../atoms/Iframe/Iframe';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';
import Picto from '../../atoms/Picto/Picto';
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import ProgressGauge from '../../atoms/ProgressGauge/ProgressGauge';
import DetailRow from '../DetailRow/DetailRow';
import DashboardTable from '../DashboardTable/DashboardTable';
import DashboardItemList from '../DashboardItemList/DashboardItemList';
import DashboardItemActions from '../DashboardItemActions/DashboardItemActions';
import DashboardItemCode from '../DashboardItemCode/DashboardItemCode';

// Styles
import styles from './DashboardItemRenderer.module.css';

/**
 * @param {string | null | undefined} help
 * @returns {JSX.Element | null}
 */
function renderHelp(help) {
  if (!help) return null;

  return (
    <Popover placement="right" isOnHover>
      <PopoverTrigger className={styles['dashboard-item-help-trigger']}>
        <Picto icon="message-question" className={styles['dashboard-item-help-icon']} />
      </PopoverTrigger>
      <PopoverContent className={styles['dashboard-item-help-popover']}>
        <div className={styles['dashboard-item-help']}>
          {help}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {React.ReactNode} content
 * @param {string | null | undefined} help
 * @returns {React.ReactNode}
 */
function renderValueWithHelp(content, help) {
  const helpNode = renderHelp(help);

  if (!helpNode) return content;

  return (
    <div className={styles['dashboard-item-value-with-help']}>
      <div className={styles['dashboard-item-value-content']}>{content}</div>
      {helpNode}
    </div>
  );
}

/**
 * Render a dashboard item based on its type
 * @param {object} props - Component props
 * @param {ApiDashboardItem} props.item - The dashboard item
 * @returns {JSX.Element | null}
 */
function DashboardItemRenderer({ item }) {
  const dashboardAction = useDashboardActionContextOptional();

  if (!item) return null;

  const { type, data } = getDashboardItemType(item);

  if (!type) {
    return (
      <DetailRow
        label=""
        value={<span className={styles['dashboard-item-error']}>{t('detailHeaderField.null')}</span>}
      />
    );
  }

  switch (type) {
    case 'text': {
      const textData = /** @type {ApiDashboardItemText} */ (data);
      return (
        <DetailRow
          label={textData.lib}
          value={renderValueWithHelp(
            <MarkdownContainer as="span" content={textData.value} />,
            textData.help,
          )}
        />
      );
    }

    case 'table': {
      const tableData = /** @type {ApiDashboardItemTable} */ (data);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <DashboardTable table={tableData.table} />,
            tableData.help,
          )}
        />
      );
    }

    case 'image': {
      const imageData = /** @type {ApiDashboardItemImage} */ (data);
      return (
        <DetailRow
          label=""
          value={(
            <img
              src={imageData.imageUrl}
              alt=""
              className={styles['dashboard-item-image']}
            />
          )}
        />
      );
    }

    case 'list': {
      const listData = /** @type {ApiDashboardItemList} */ (data);
      return (
        <DetailRow
          label={listData.lib}
          value={renderValueWithHelp(
            <DashboardItemList items={listData.items} />,
            listData.help,
          )}
        />
      );
    }

    case 'chartLine': {
      const chartLineData = /** @type {ApiDashboardItemChartLine} */ (data);
      const lineChart = mapLineChartProps(chartLineData);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <ChartLine
              rows={lineChart.rows}
              series={lineChart.series}
              referenceLines={lineChart.referenceLines}
              legend={lineChart.legend}
            />,
            chartLineData.help,
          )}
        />
      );
    }

    case 'chartBar': {
      const chartBarData = /** @type {ApiDashboardItemChartBar} */ (data);
      const barChart = mapBarChartProps(chartBarData);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <ChartBar
              rows={barChart.rows}
              series={barChart.series}
              referenceLines={barChart.referenceLines}
              legend={barChart.legend}
              layout={barChart.layout}
              stacked={barChart.stacked}
              categoryColors={barChart.categoryColors}
            />,
            chartBarData.help,
          )}
        />
      );
    }

    case 'chartPie': {
      const chartPieData = /** @type {ApiDashboardItemChartPie} */ (data);
      const pieChart = mapPieChartProps(chartPieData);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <ChartPie
              slices={pieChart.slices}
              legend={pieChart.legend}
              showValuesInLegend={pieChart.showValuesInLegend}
              donut={pieChart.donut}
            />,
            chartPieData.help,
          )}
        />
      );
    }

    case 'progress': {
      const progressData = /** @type {ApiDashboardItemProgress} */ (data);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <ProgressGauge gauges={progressData.gauges} />,
            progressData.help,
          )}
        />
      );
    }

    case 'actions': {
      const actionsData = /** @type {ApiDashboardItemActions} */ (data);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <DashboardItemActions
              actions={actionsData.actions}
              onActionClick={dashboardAction?.handleActionClick}
              pendingActionId={dashboardAction?.pendingActionId ?? null}
            />,
            actionsData.help,
          )}
        />
      );
    }

    case 'iframe': {
      const iframeData = /** @type {ApiDashboardItemIframe} */ (data);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <Iframe src={iframeData.url} title={t('dashboard.iframe.title')} />,
            iframeData.help,
          )}
        />
      );
    }

    case 'code': {
      const codeData = /** @type {ApiDashboardItemCode} */ (data);
      return (
        <DetailRow
          fullWidth
          label=""
          value={renderValueWithHelp(
            <DashboardItemCode id={codeData.id} type={codeData.type} value={codeData.value} />,
            codeData.help,
          )}
        />
      );
    }

    case 'boolean': {
      const booleanData = /** @type {ApiDashboardItemBoolean} */ (data);
      return (
        <DetailRow
          label=""
          value={renderValueWithHelp(<p>Not implemented</p>, booleanData.help)}
        />
      );
    }

    default:
      return null;
  }
}

export default DashboardItemRenderer;
